import { cn } from '@blink/ui';
import { CircleHelp } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import tips from '@/help/help-tips.json';

type Tip = { text: string; slug: string; anchor: string; title: string };

/** The small "?" beside a step or button: a short explanation and a link to Help. */
export function HelpTip({ id, className }: { id: string; className?: string }) {
  const tip = (tips as Record<string, Tip>)[id];
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!tip) return null;
  return (
    <span ref={box} className={cn('relative inline-flex align-middle', className)}>
      <button
        type="button"
        aria-label="Help"
        aria-expanded={open}
        aria-controls={popId}
        data-testid={`help-tip-${id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full opacity-80 hover:opacity-100"
      >
        <CircleHelp className="h-5 w-5" />
      </button>
      {open ? (
        <span id={popId} role="dialog" className="absolute end-0 top-8 z-50 w-64 rounded-lg border bg-card p-3 text-start text-sm font-normal leading-relaxed text-foreground shadow-lg">
          <span className="block">{tip.text}</span>
          <Link to={`/help/${tip.slug}${tip.anchor ? `#${tip.anchor}` : ''}`} className="mt-2 block font-semibold text-primary underline" onClick={() => setOpen(false)}>
            More help
          </Link>
        </span>
      ) : null}
    </span>
  );
}
